import { useEffect } from "react";
import { drawingsSettled } from "@/artifacts/pending";

declare global {
  interface Window {
    /** Convenience for a human at a console; the renderer reads the attribute. */
    __mingPrintReady?: boolean;
  }
}

/**
 * The signal itself: an attribute on `<html>`.
 *
 * **Not** a global the renderer evaluates. This deployment's own CSP is
 * `script-src 'self'` with no `unsafe-eval`, and a headless browser asked to
 * wait on a *string* expression evaluates it with `eval` — which the CSP
 * refuses. The wait then fails instantly and the renderer prints whatever is
 * on screen, which on a page that has not finished booting is the unlock
 * screen: a branded, plausible PDF of a login form. A DOM attribute is waited
 * on with a selector, which needs no page-side eval and cannot be blocked.
 */
const READY_ATTRIBUTE = "data-print-ready";

/**
 * Tell the renderer this page has finished drawing itself (PRD FR-9.1).
 *
 * The alternative — waiting for the network to go quiet — photographs a
 * half-drawn page: ECharts paints to a canvas *after* its data arrives, and a
 * webfont swapping in a beat later reflows every heading. So the page raises a
 * flag when it is genuinely done, and the renderer waits for that flag.
 *
 * Four things have to have happened, in this order:
 *
 * 1. the data is loaded — the caller decides that;
 * 2. the chart chunk has arrived, if this page has any charts. It is fetched
 *    here with the same dynamic import `ArtifactCard` uses, deliberately: a
 *    static one would pull ECharts into the entry bundle for the whole app and
 *    make the unlock page pay for it (PRD NFR-1) — and any map has drawn,
 *    which needs its boundaries as well as its chunk (`artifacts/pending`);
 * 3. the fonts are ready, so nothing reflows after the shutter; and
 * 4. two animation frames plus a settling pause, which is what it takes for
 *    the canvases mounted in step 2 to have actually painted.
 *
 * The attribute is removed on cleanup, so a route change cannot leave a stale
 * "ready" behind for the next page.
 */
export function usePrintReady(loaded: boolean, hasCharts: boolean, maps = 0) {
  useEffect(() => {
    document.documentElement.removeAttribute(READY_ATTRIBUTE);
    window.__mingPrintReady = false;
    if (!loaded) return;
    let cancelled = false;

    async function settle() {
      if (hasCharts) {
        await import("@/components/artifacts/ChartCanvas").catch(() => undefined);
        // Let the canvases mount, then wait for any map still loading its
        // boundaries (MAP-2) — a map cannot paint two frames after its chunk.
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve(null))),
        );
        await drawingsSettled(maps);
      }
      await document.fonts?.ready?.catch?.(() => undefined);
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve(null))),
      );
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      if (cancelled) return;
      window.__mingPrintReady = true;
      document.documentElement.setAttribute(READY_ATTRIBUTE, "true");
    }

    void settle();
    return () => {
      cancelled = true;
      window.__mingPrintReady = false;
      document.documentElement.removeAttribute(READY_ATTRIBUTE);
    };
  }, [loaded, hasCharts, maps]);
}
